'use client'
import React, { Component, ReactNode } from 'react'
import { reportClientError } from '@/utils/clientErrorReporter'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode

  name: string
}

interface ErrorBoundaryState {
  hasError: boolean
}

const MAX_COMPONENT_STACK_CHARS = 3000

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    reportClientError({
      message: error.message,
      stack: error.stack,

      digest: (error as Error & { digest?: string }).digest,
      errorName: error.name,
      componentStack: errorInfo.componentStack ?? undefined,
      boundary: this.props.name,
      source: 'section-error-boundary',
    })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) return this.props.fallback

      return (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 text-center">
          <p className="text-body-sm text-gray-700">
            Something went wrong loading this section.
          </p>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
